import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import {
  adminCreateReport,
  adminGetReport,
  adminUpdateReport,
  getInsightSeries,
} from "../lib/api";
import { ADMIN } from "../constants/testIds";
import { ArrowLeft } from "lucide-react";

const empty = {
  slug: "",
  title: "",
  subtitle: "",
  category: "Owner Briefings",
  summary: "",
  topics: "",
  sources: "",
  cover_image_url: "",
  content: "",
  featured: false,
  published: true,
};

const AdminReportEdit = () => {
  const { id } = useParams();
  const isNew = !id;
  const [series, setSeries] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    getInsightSeries().then(setSeries);
    if (!isNew) {
      adminGetReport(id)
        .then((r) =>
          setForm({
            ...r,
            topics: (r.topics || []).join(", "),
            sources: (r.sources || []).join(", "),
          })
        )
        .catch((e) => {
          if (e.response?.status === 401) navigate("/admin/login");
        });
    }
  }, [id, isNew, navigate]);

  const setField = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        topics: form.topics.split(",").map((s) => s.trim()).filter(Boolean),
        sources: form.sources.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isNew) await adminCreateReport(payload);
      else await adminUpdateReport(id, payload);
      navigate("/admin");
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <Link to="/admin" className="font-mono-label text-[var(--slate)] inline-flex items-center gap-2 hover:text-[var(--accent)] transition-colors mb-8">
          <ArrowLeft size={14} /> Back
        </Link>
        <h1 className="font-display text-4xl text-[var(--ivory)] mb-10">
          {isNew ? "New report" : "Edit report"}
        </h1>

        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <label className="font-mono-label text-[var(--slate)] mb-2 block">Title</label>
            <input className="input-field" required value={form.title} onChange={setField("title")} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-mono-label text-[var(--slate)] mb-2 block">Slug</label>
              <input className="input-field" value={form.slug} onChange={setField("slug")} placeholder="auto-generated if empty" />
            </div>
            <div>
              <label className="font-mono-label text-[var(--slate)] mb-2 block">Insight Series</label>
              <select className="input-field" value={form.category} onChange={setField("category")}>
                {series.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="font-mono-label text-[var(--slate)] mb-2 block">Subtitle</label>
            <input className="input-field" value={form.subtitle} onChange={setField("subtitle")} />
          </div>
          <div>
            <label className="font-mono-label text-[var(--slate)] mb-2 block">Summary</label>
            <textarea className="input-field" rows={3} value={form.summary} onChange={setField("summary")} />
          </div>
          <div>
            <label className="font-mono-label text-[var(--slate)] mb-2 block">Cover image URL</label>
            <input className="input-field" value={form.cover_image_url} onChange={setField("cover_image_url")} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-mono-label text-[var(--slate)] mb-2 block">Topics (comma-separated)</label>
              <input className="input-field" value={form.topics} onChange={setField("topics")} />
            </div>
            <div>
              <label className="font-mono-label text-[var(--slate)] mb-2 block">Sources (comma-separated)</label>
              <input className="input-field" value={form.sources} onChange={setField("sources")} />
            </div>
          </div>
          <div>
            <label className="font-mono-label text-[var(--slate)] mb-2 block">Content (markdown)</label>
            <textarea
              className="input-field font-mono text-sm"
              rows={16}
              value={form.content}
              onChange={setField("content")}
              placeholder={isNew ? "Leave empty to auto-generate via AI" : ""}
            />
          </div>
          <div className="flex items-center gap-8">
            <label className="flex items-center gap-2 text-sm text-[var(--ivory)]">
              <input type="checkbox" checked={form.featured} onChange={setField("featured")} /> Featured
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--ivory)]">
              <input type="checkbox" checked={form.published} onChange={setField("published")} /> Published
            </label>
          </div>

          {err && <div className="text-sm text-red-300">{err}</div>}

          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary" data-testid={ADMIN.saveReport}>
              {saving ? "Saving…" : "Save report"}
            </button>
            <Link to="/admin" className="btn-ghost">Cancel</Link>
          </div>
        </form>
      </section>
    </Layout>
  );
};

export default AdminReportEdit;
